import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';

const useGuestPolling = (guestId, interval = 5000) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const previousSuggestionsRef = useRef({});
  const pollingIntervalRef = useRef(null);

  const fetchSuggestions = useCallback(async () => {
    if (!guestId) return;

    try {
      const response = await api.get(`/guests/${guestId}/suggestions`);
      const newSuggestions = response.data;

      // Check for status changes and show notifications
      newSuggestions.forEach(suggestion => {
        const previous = previousSuggestionsRef.current[suggestion.id];
        
        if (previous && previous.status !== suggestion.status) {
          // Status changed - show notification
          if (suggestion.status === 'accepted') {
            toast.success(`✅ ${suggestion.requested_title} a été acceptée ! 🎉`, {
              duration: 5000
            });
          } else if (suggestion.status === 'rejected') {
            toast.error(`❌ ${suggestion.requested_title} a été rejetée`, {
              duration: 5000
            });
          } else if (suggestion.status === 'played') {
            toast.success(`🎵 ${suggestion.requested_title} est en cours de lecture !`, {
              duration: 5000
            });
          }
        }

        previousSuggestionsRef.current[suggestion.id] = suggestion;
      });

      setSuggestions(newSuggestions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      if (error.response?.status === 404 || error.response?.status === 403) {
        // Session expired or not found
        toast.error('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem('guestSession');
        window.location.reload();
      }
      setLoading(false);
    }
  }, [guestId]);

  useEffect(() => {
    if (!guestId) {
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchSuggestions();

    // Start polling
    pollingIntervalRef.current = setInterval(fetchSuggestions, interval);

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [guestId, interval, fetchSuggestions]);

  // Pause polling when page is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      } else {
        // Resume polling when page becomes visible
        fetchSuggestions();
        pollingIntervalRef.current = setInterval(fetchSuggestions, interval);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSuggestions, interval]);

  return {
    suggestions,
    loading,
    refreshSuggestions: fetchSuggestions
  };
};

export default useGuestPolling;
